import { Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { ProcessingFailure } from '@/services/api/adminProcessing'

/**
 * Phone layout of the failures table: one stacked block per failure, so the error (the column
 * that matters) is readable without scrolling sideways. The error is clamped and opens on tap,
 * since the desktop hover tooltip does not exist on a touch screen.
 */
export function FailuresMobileList({
  failures,
  onCopy,
}: {
  failures: ProcessingFailure[]
  onCopy: (error: string) => void
}) {
  const { t } = useTranslation()
  return (
    <ul className="divide-y divide-border md:hidden">
      {failures.map((f, i) => (
        <li key={`${f.job_id}-${f.step_name}-${i}`} className="space-y-1 px-2 py-2.5">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="text-xs text-muted-foreground">
                {f.ended_at ? new Date(f.ended_at).toLocaleString() : '—'}
              </div>
              <div className="font-mono text-xs wrap-anywhere">
                {f.job_id.slice(0, 8)} · {f.step_name ?? '—'}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => onCopy(f.error)}
              aria-label={t('admin.processing.failures.copyError')}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
          <details className="group/error">
            <summary className="line-clamp-3 cursor-pointer list-none text-xs text-destructive wrap-anywhere group-open/error:line-clamp-none [&::-webkit-details-marker]:hidden">
              {f.error}
            </summary>
          </details>
        </li>
      ))}
    </ul>
  )
}
